<template>
	<BaseLayout
		:footer-full="true"
		:background-color-class="'bg-color-white'"
		:include-padding="false"
		:footnotes="footnotes"
	>
		<div class="rei-guide position-relative" role="main">
			<header
				class="rei-guide-header display-flex align-items-center text-align-center container pt-600 pb-600 pb-0-lg"
			>
				<div v-reveal:fade-up class="col-10-md col-8-lg col-offset-1-md col-offset-2-lg pb-600">
					<p class="overline mb-100">Real Estate Investing Guide</p>
					<h1 class="heading-xl">Why real estate investing?</h1>
					<p class="rei-guide-header__summary delta-md line-height-150">
						With its ability to earn consistent income, track record of long-term appreciation, and capacity
						to diversify an investment portfolio, real estate is used by many smart investors to build
						wealth. In fact, those who diversify a portion of their portfolio in real estate investments
						have historically outperformed those who haven’t.<sup>1</sup>
					</p>
				</div>
			</header>

			<section class="rei-guide-content position-relative">
				<div
					class="container display-flex display-block-lg grid-lg flex-wrap-wrap justify-content-center pl-0 pr-0 pl-300-lg pr-300-lg"
				>
					<div class="col-10-md col-8-lg pl-100 pr-100">
						<REIGuideSectionArticle :section="reiBenefits">
							<div
								v-for="(block, id) in iconBlocks"
								:key="id"
								class="icon-content-block pt-100 pb-100 pl-600-md"
							>
								<h2 :class="'heading--' + block.slug" class="icon-content-block__heading heading-5">
									{{ block.heading }}
								</h2>
								<p v-for="(content, c) in block.summary" :key="c" class="epsilon" v-html="content"></p>
								<div v-if="block.figure">
									<h3 class="heading-6 mt-100 mb-100">{{ block.figure.heading }}</h3>
									<figure class="ml-0 mt-0 mr-0 mb-0">
										<img :src="block.figure.src" :alt="block.figure.heading" />
										<figcaption class="eta muted mt-50">
											<p
												v-for="(caption, i) in block.figure.captions"
												:key="i"
												v-html="caption"
											></p>
										</figcaption>
									</figure>
								</div>
								<BaseCtaLink :link="block.link" classes="display-inline-block mt-100" icon="arrow" />
							</div>
						</REIGuideSectionArticle>

						<REIGuideSectionArticle :section="reiStocks">
							<h2 class="heading-5 mt-100 mt-200-lg">More consistency, less volatility</h2>
							<p class="epsilon">
								Private market real estate has little correlation with stock market performance because
								they’re traded in separate markets. This difference, plus less efficient dynamics of the
								private market and far fewer large daily changes in values, has made its performance far
								less volatile and more stable than the stock market.
							</p>
							<h3 class="heading-6 mt-100 text-align-center">
								Trends in real returns on equity and housing
							</h3>
							<figure class="trends-graphic ml-0 mt-0 mr-0 mb-0">
								<img
									:src="
										appCloudfrontPath +
										'/images/marketing-site/reiguide/trends-in-realestate-chart.jpg'
									"
									alt="Trends in real returns on equity and housing"
								/>
								<figcaption class="eta muted mt-50">
									<p>
										Note: Mean returns for 16 countries, weighted by real GDP. Decadal moving
										averages
									</p>
									<p>
										Source: The Rate of Return on Everything, 1870–2015 by the Federal Reserve Bank
										of San Francisco
									</p>
								</figcaption>
							</figure>
							<p class="epsilon">
								In comparison, the number of publicly traded stocks in the US is shrinking and
								correlation is growing leading to broader volatility.<sup>7</sup> Between 1950 and 1999,
								there were 81 daily movements of 3% or more in the S&amp;P 500.<sup>8</sup> Between 2000
								and 2018, there were 120 daily movements of 3% or more.<sup>9</sup>
							</p>
							<BaseCtaLink :link="blogLessVolatile" classes="display-inline-block mt-100" icon="arrow" />
							<h2 class="heading-5 mt-100 mt-200-lg">Inflation Hedging</h2>
							<p class="epsilon">
								As a hard asset, real estate is one of the few assets that can hedge inflation. Because
								land is naturally limited in supply, it holds intrinsic value. As land becomes scarcer,
								especially in high-demand areas, its value appreciates. Residential land values in the
								US have increased by 31% over the ten-year span between 2008 and 2018, and by 169% over
								the 20-year span between 1998 and 2018.<sup>10, 11</sup> Real estate is a unique hard
								asset because rising demand can not only increase its value, but also its rental price
								potential.
							</p>
							<BaseCtaLink :link="blogHardAsset" classes="display-inline-block mt-100" icon="arrow" />
						</REIGuideSectionArticle>

						<REIGuideSectionArticle :section="reiEarnMoney">
							<img
								:src="appCloudfrontPath + '/images/marketing-site/reiguide/Real-Estate-101.png'"
								alt="Real estate can earn money over the short term and long term through income as well as appreciation."
							/>

							<div v-if="reiEarnMoneyContent" class="grid gutters-300-md">
								<div v-for="(content, id) in reiEarnMoneyContent" :key="id" class="col-6-lg">
									<h2 class="heading-5 mt-100 mt-200-lg">{{ content.heading }}</h2>
									<p
										v-for="(p, earnParagraphId) in content.paragraph"
										:key="earnParagraphId"
										class="epsilon"
										v-html="p"
									></p>
									<BaseCtaLink
										:link="content.link"
										classes="display-inline-block mt-100"
										icon="arrow"
									/>
								</div>
							</div>
						</REIGuideSectionArticle>

						<REIGuideSectionArticle :section="reiTaxAdvantages">
							<p class="epsilon">
								Taxation amounts and timing for each investor are impacted by several factors, including
								the amount and type of return earned, investment duration, investment vehicle, and other
								factors. For that reason, it’s important to consult your tax advisor concerning taxes
								for your specific investments.
							</p>
							<img
								:src="
									appCloudfrontPath + '/images/marketing-site/reiguide/Reits101-Passthru-Income.png'
								"
								alt=""
								role="presentation"
							/>

							<p class="epsilon">
								On a basic level, any income and capital gains earned from appreciation will likely be
								taxed at some point. For example, as of 2018, income earned and distributed by REITs and
								real estate mutual funds can qualify for up to a
								<BaseLink :link="blogTaxCode" :classes="'text-link'" />. The pass-through structure of
								these investment vehicles allows their investors to claim this deduction on qualifying
								business income under the Tax Cuts and Jobs Acts.
							</p>
						</REIGuideSectionArticle>

						<REIGuideSectionArticle :section="reiLengthOfInvestment">
							<div class="display-flex flex-wrap-wrap gutters-300-md">
								<div class="mt-150 col-8-lg">
									<p class="epsilon">
										Real estate is usually an illiquid investment, especially in the private market.
										This characteristic means that properties are slow to be sold, but it can also
										mitigate the volatility found in assets with higher liquidity, like publicly
										traded stocks and bonds.
									</p>

									<p class="epsilon">
										The holding period or time horizon for real estate is generally several years.
										Institutional investors hold commercial real estate an average of 7.6 years,
										while direct investors who own residential investment properties may hold them
										even longer.<sup>12</sup> At Fundrise, we advise investors to expect a holding
										period of at least five years.
									</p>
								</div>
								<div
									class="order--1 order-1-lg reiguide-callout-block mt-150-lg col-4-lg"
									role="complementary"
								>
									<div class="grid gutters-0-lg bg-color-greige pt-150 pl-150 pr-150 pb-150">
										<div class="col-6 col-12-lg pr-50 pr-0-lg">
											<p class="eta">
												<span class="heading-5 display-block text-color-content-secondary"
													>7.6 years</span
												>average time that institutional investors hold commercial real estate
											</p>
										</div>
										<div class="col-6 col-12-lg mt-100-lg pl-50 pl-0-lg">
											<p class="eta">
												<span class="heading-5 display-block text-color-content-secondary"
													>5 years</span
												>minimum holding period Fundrise recommends for its investors
											</p>
										</div>
									</div>
								</div>
							</div>
						</REIGuideSectionArticle>

						<REIGuideSectionArticle :section="reiOptions">
							<div v-if="reiOptionsContent" class="grid gutters-300-lg">
								<div
									v-for="(content, optionsId) in reiOptionsContent"
									:key="optionsId"
									class="col-6-lg"
								>
									<h2 class="heading-5 mt-100">{{ content.heading }}</h2>
									<p class="epsilon mt-50" v-html="content.paragragraph"></p>
									<BaseCtaLink
										:link="content.link"
										classes="display-inline-block mt-100"
										icon="arrow"
									/>
								</div>
							</div>
						</REIGuideSectionArticle>
					</div>

					<InPageNav
						:nav-info="inArticleNavObj"
						classes="col-4-lg order--1 order-2-lg mt-500-lg mb-600-lg ml-auto-lg"
					/>
				</div>
			</section>

			<section class="marketing-section bg-color-content-primary pl-100 pr-100">
				<div class="container text-align-center text-color-white">
					<div class="col-10-md col-8-lg col-offset-1-md col-offset-2-lg">
						<h2 class="heading-1 mb-100 text-color-white">Considering investing?</h2>
						<p class="epsilon mb-100">
							Depending on which way you choose to invest in real estate, you’ll need varying amounts of
							time, knowledge, and capital. Fundrise offers a simple, low-cost way to invest in real
							estate whether you’re investing {{ currency(1000, 0) }} or {{ currency(100000, 0) }}.
						</p>
						<BaseLink :link="exploreFundrise" :classes="'button button-primary'" />
					</div>
				</div>
			</section>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { appBasePath, appCloudfrontPath } from '@constants';
import { InPageNavInfo, Link, REIGuideSection } from 'types/layout';
import { computed } from 'vue';
import { currency } from '@filters/shared-filters';
import InPageNav from '@components/layout/in-page-nav.vue';
import REIGuideSectionArticle from '@components/marketing-site/rei-guide-section.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';

const appStore = useAppStore();

const metaTitle = 'Real Estate Investing Guide';
const metaDescription = `Real estate offers many benefits, but one option doesn't fit all investors. Use this comprehensive real estate investing guide to learn more before starting.`;

const reiSections = [
	{
		slug: 'rei-benefits',
		heading: 'What are the benefits of investing in real estate?'
	},
	{
		slug: 'rei-stocks',
		heading: 'What does real estate offer that stocks and bonds don’t?'
	},
	{
		slug: 'rei-earn-money',
		heading: 'How do real estate investments earn money?'
	},
	{
		slug: 'rei-tax-advantages',
		heading: 'What are the tax advantages of real estate investments?'
	},
	{
		slug: 'rei-length-of-investment',
		heading: 'How long do real estate investments last? '
	},
	{
		slug: 'rei-options',
		heading: 'What are the options to invest in real estate?'
	}
];

const reiBenefits: REIGuideSection = {
	slug: reiSections[0].slug,
	heading: reiSections[0].heading,
	subhead:
		'Real estate features a unique combination of short-term and long-term earning potential as well as diversification power not found in other asset classes.'
};

const reiStocks: REIGuideSection = {
	slug: reiSections[1].slug,
	heading: reiSections[1].heading,
	subhead:
		'Real estate investments typically complement portfolios heavily allocated to stocks and bonds, because their distinct characteristics can bring advantages not found in the stock market.'
};

const reiEarnMoney: REIGuideSection = {
	slug: reiSections[2].slug,
	heading: reiSections[2].heading,
	subhead: 'Real estate can earn money over the short term and long term through income as well as appreciation.'
};

const reiEarnMoneyContent = [
	{
		heading: 'Income',
		paragraph: [
			'Debt investments, such as a real estate loan, can earn interest typically at a fixed rate of return. Loans are often less risky than equity investments, which make them attractive for those seeking income.',
			'Equity investments can earn income through rental payments. As a long-term investment, real estate investors with equity ownership can earn significant income over time depending on market demand and occupancy.'
		],
		link: {
			text: 'Learn more',
			router: 'blog-post',
			hash: '#how-real-estate-investments-earn-income',
			params: {
				blogPostUrlSlug: 'how-real-estate-investments-earn-money'
			}
		}
	},
	{
		heading: 'Appreciation',
		paragraph: [
			'While an investment property collects income from paying tenants, the property itself can gain value as well. When the property is sold, it may earn returns for equity investors in a lump sum payment — in addition to any of the income previously earned over the course of the investment’s lifetime.'
		],
		link: {
			text: 'Learn more',
			router: 'blog-post',
			hash: '#how-real-estate-investments-earn-appreciation',
			params: {
				blogPostUrlSlug: 'how-real-estate-investments-earn-money'
			}
		}
	}
];

const reiTaxAdvantages: REIGuideSection = {
	slug: reiSections[3].slug,
	heading: reiSections[3].heading,
	subhead:
		'Real estate investments can offer useful tax advantages, but like most investments, tax consequences depend on several factors, including the investment vehicle.',
	link: {
		text: 'Learn more',
		router: 'blog-post',
		hash: '#real-estate-investment-taxes',
		params: {
			blogPostUrlSlug: 'how-to-invest-in-real-estate-the-basics'
		}
	}
};

const reiLengthOfInvestment: REIGuideSection = {
	slug: reiSections[4].slug,
	heading: reiSections[4].heading,
	subhead:
		'Real estate is generally a long-term investment with the highest earning potential available typically over the course of several years.',
	link: {
		text: 'Learn more',
		router: 'blog-post',
		params: {
			blogPostUrlSlug: 'how-to-determine-your-time-horizon'
		}
	}
};

const reiOptions: REIGuideSection = {
	slug: reiSections[5].slug,
	heading: reiSections[5].heading,
	subhead:
		'Investment options break down into two categories: active (hands-on) and passive (hands-off). Each approach has distinct advantages and disadvantages.'
};

const reiOptionsContent = [
	{
		heading: 'Active',
		paragragraph:
			'Active real estate is the direct ownership of an asset. This approach gives investors more control, but also greater responsibility and exposure to risk. Active investments, like rental properties, wholesaling, and house-flipping, are often high-effort, time-intensive, and require some amount of expertise to be successful.',
		link: {
			text: 'Learn more',
			router: 'blog-post',
			hash: '#active-real-estate-investing',
			params: {
				blogPostUrlSlug: 'how-to-invest-in-real-estate-the-basics'
			}
		}
	},
	{
		heading: 'Passive',
		paragragraph:
			'Passive real estate investors provide only the capital and let professionals invest in real estate on their behalf — often through a fund. Passive investors bear less responsibility and generally have greater access to <a href="' +
			appBasePath +
			'/assets" class="text-link">investments of various types and sizes</a>. Options like REITs, mutual funds, and real estate investment platforms are typically low-effort and hands-off, without requiring expertise.',
		link: {
			text: 'Learn more',
			router: 'blog-post',
			hash: '#passive-real-estate-investing',
			params: {
				blogPostUrlSlug: 'how-to-invest-in-real-estate-the-basics'
			}
		}
	}
];

const blogLessVolatile: Link = {
	text: 'Learn more',
	router: 'blog-post',
	params: {
		blogPostUrlSlug: 'why-real-estate-is-less-volatile-than-the-stock-market'
	}
};

const blogHardAsset: Link = {
	text: 'Learn more',
	router: 'blog-post',
	params: {
		blogPostUrlSlug: 'real-estate-is-a-hard-asset'
	}
};

const blogTaxCode: Link = {
	text: '20% tax deduction',
	router: 'blog-post',
	params: {
		blogPostUrlSlug: 'a-new-tax-code-is-now-law-and-presents-opportunities-for-real-estate-invest'
	}
};

const exploreFundrise: Link = {
	text: 'Explore Fundrise',
	router: 'how-it-works'
};

const footnotes: Array<string> = [
	'Blackstone, <a href="https://www.blackstone.com/docs/default-source/black-papers/seeking-an-alternative_standard.pdf" target="_blank" class="text-color-current-color">"Seeking an Alternative: Understanding and Allocating to Alternative Investments"</a>, 15',
	'This compares the aggregate market value of homes (MKVAL_NOM) between 2008Q2 and 2018Q2 using data from the <a href="https://www.aei.org/historical-land-price-indicators/" target="_blank" class="text-color-current-color">“Quarterly Dataset based on FHFA House Price Index, 1975:Q1-2018:Q2”</a> from the American Enterprise Institute.',
	'This compares the differences in the aggregate market value of homes (MKVAL_NOM) between 1998Q2 and 2018Q2 using data from the <a href="https://www.aei.org/historical-land-price-indicators/" target="_blank" class="text-color-current-color">“Quarterly Dataset based on FHFA House Price Index, 1975:Q1-2018:Q2”</a> from the American Enterprise Institute.',
	'This compares the differences in commercial property values represented in the <a href="https://www.greenstreetadvisors.com/insights/CPPI" target="_blank" class="text-color-current-color">Green Street Commercial Property Price Index</a> between December 2008 and December 2018',
	'This compares the differences in commercial property values represented in the <a href="https://www.greenstreetadvisors.com/insights/CPPI" target="_blank" class="text-color-current-color">Green Street Commercial Property Price Index</a> between December 1998 and December 2018.',
	'Blackstone, <a href="https://www.breit.com/wp-content/uploads/sites/4/2020/12/BREIT-Why-Private-Real-Estate.pdf" target="_blank" class="text-color-current-color">“Why Private Real Estate?”</a>',
	'World Bank, <a href="https://data.worldbank.org/indicator/CM.MKT.LDOM.NO?locations=US" target="_blank" class="text-color-current-color">“Listed Domestic Companies, Total – United States”</a>',
	'Blackstone, <a href="https://www.blackstone.com/docs/default-source/black-papers/seeking-an-alternative_standard.pdf" target="_blank" class="text-color-current-color">"Seeking an Alternative: Understanding and Allocating to Alternative Investments"</a>, 5',
	'Blackstone, <a href="https://www.blackstone.com/docs/default-source/black-papers/seeking-an-alternative_standard.pdf" target="_blank" class="text-color-current-color">"Seeking an Alternative: Understanding and Allocating to Alternative Investments"</a>, 5',
	'This compares the aggregate market value of residential land (LAND_NOM) between 2008Q2 and 2018Q2 using data from the <a href="https://www.aei.org/historical-land-price-indicators/" target="_blank" class="text-color-current-color">“Quarterly Dataset based on FHFA House Price Index, 1975:Q1-2018:Q2”</a> from the American Enterprise Institute.',
	'This compares the differences in the aggregate market value of residential land (LAND_NOM) between 1998Q2 and 2018Q2 using data from the <a href="https://www.aei.org/historical-land-price-indicators/" target="_blank" class="text-color-current-color">“Quarterly Dataset based on FHFA House Price Index, 1975:Q1-2018:Q2”</a> from the American Enterprise Institute.',
	'Fisher, Jeffrey, Dunn, Barbara, <a href="https://www.msyapps.com/MSY/articlesPDF/PropTen.pdf" target="_blank" class="text-color-current-color">“Holding Periods for Institutional Real Estate in the NCREIF Database”</a>, 9'
];

const inArticleNavObj: InPageNavInfo = {
	routeName: 'rei-guide',
	anchors: reiSections
};

const diversificationChart = computed((): string => {
	const desktopSrc = appCloudfrontPath + '/images/marketing-site/reiguide/diversification-chart.jpg';
	const mobileSrc = appCloudfrontPath + '/images/marketing-site/reiguide/diversification-chart-mobile.jpg';
	return appStore.isDesktop ? desktopSrc : mobileSrc;
});

const iconBlocks = computed(() => [
	{
		slug: 'cash-flow',
		heading: 'Consistent Cash Flow',
		summary: [
			'For many investors, the ability to create consistent cash flow is one of the most attractive aspects of real estate investing. Some examples include equity ownership in apartment buildings that earn income through rental payments or loans that earn income through interest payments. While smaller equity investments, such as homes, can be bought directly, other investment vehicles, such as funds, give investors of all sizes access to larger income-producing assets.'
		],
		link: {
			text: 'Learn more',
			router: 'blog-post',
			hash: '#how-real-estate-investments-earn-income',
			params: {
				blogPostUrlSlug: 'how-real-estate-investments-earn-money'
			}
		}
	},
	{
		slug: 'gain-potential',
		heading: 'Long-Term Gain Potential',
		summary: [
			'Equity ownership offers unique potential for generating both cash flow and long-term appreciation. Both residential and commercial real estate have track records of long-term appreciation, which may match with a long-term investor’s portfolio.',
			'The market value of residential real estate in the US increased by 36% over the ten-year span between 2008 and 2018, and by 173% over the 20-year span between 1998 and 2018.<sup>2, 3</sup> Commercial real estate in the US also increased by 88% between 2008 and 2018 and by 159% between 1998 and 2018.<sup>4, 5</sup>'
		],
		link: {
			text: 'Learn more',
			router: 'blog-post',
			hash: '#how-real-estate-investments-earn-appreciation',
			params: {
				blogPostUrlSlug: 'how-real-estate-investments-earn-money'
			}
		}
	},
	{
		slug: 'diversification',
		heading: 'Diversification',
		summary: [
			'As an asset class with low correlation with the stock market, private real estate offers meaningful diversification. By diversifying an investment portfolio among and across an uncorrelated asset class, such as private real estate, an investor reduces exposure to unnecessary risk of loss, which helps to create a more stable and profitable portfolio.<sup>6</sup>'
		],
		figure: {
			heading: '20% Rule: Higher return potential at lower levels of volatility',
			src: diversificationChart.value,
			captions: [
				'**Hypothetical portfolios represented by S&amp;P 500 (equity), Barclays Capital US Aggregate Bond (fixed income)<br /> *Hypothetical portfolios represented by S&amp;P 500 (equity), Barclays Capital US Aggregate Bond (fixed income), and an equal weighted blend of alternatives comprised of HFRI Fund Weighted Composite (hedge funds).<br /> Barclays CTA (managed futures), and Cambridge U.S. Private Equity (private equity). Past performance is not a guarantee of future performance.',
				'Source: The Role of Alternative Investments in a Diversified Investment Portfolio by Baird Private Wealth Management'
			]
		},
		link: {
			text: 'Learn more',
			router: 'blog-post',
			params: {
				blogPostUrlSlug: 'how-to-enhance-portfolio-diversification-using-real-estate'
			}
		}
	}
]);

function setup(): void {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription },
			{
				property: 'og:image',
				content: `${appCloudfrontPath}/images/marketing-site/reiguide/reiguide_og.png`
			}
		]
	});
}
setup();
</script>

<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;

.rei-guide {
	.rei-offset-anchor {
		// offset against sticky elements
		position: absolute;
		top: -95px;

		@include respond-to(lg) {
			top: -75px;
		}
	}
	.heading-2 {
		letter-spacing: -0.7px;
	}
	.trends-graphic {
		margin: 0 auto;
		max-width: 530px;
	}
	&-callout-block {
		@include respond-to(mdo) {
			width: 100%;
		}
	}
	&-content-section {
		+ .rei-guide-content-section {
			padding-top: 0;
		}
	}
}

.rei-guide-header {
	background-color: #fff;
	background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/citymountains_sm.png');
	background-position: 50% 105%;
	background-repeat: no-repeat;
	background-size: contain;
	min-height: 100vh;
	position: relative;

	@include respond-to(md) {
		background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/citymountains_lg.png');
		background-size: 95%;
	}

	@include respond-to(lg) {
		min-height: calc(100vh - 60px);
		padding-bottom: 300px;
	}

	&::before,
	&::after {
		background: transparent center no-repeat;
		background-size: contain;
		content: '';
		position: absolute;

		@include respond-to(md) {
			animation: sway 8s ease-in-out infinite;
		}
	}

	&::before {
		background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/cloud_right.png');
		height: 60px;
		max-width: 241px;
		right: 1.5rem;
		top: 15%;
		width: 40%;

		@include respond-to(lg) {
			height: 87px;
			right: 13%;
			top: 3rem;
			width: 20%;
		}
	}

	&::after {
		animation-delay: 4s;
		background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/cloud_left.png');
		height: 50px;
		max-width: 160px;
		left: -5px;
		top: 35%;
		width: 40%;

		@include respond-to(lg) {
			height: 108px;
			left: 3rem;
			max-width: 340px;
			top: 25%;
			width: 25%;
		}
	}

	> div {
		position: relative;
		z-index: +1;
	}

	&__summary {
		letter-spacing: 0.2px;
	}
}

.icon-content-block {
	.icon-content-block__heading {
		position: relative;

		&::before {
			background-color: transparent;
			background-position: center left;
			background-repeat: no-repeat;
			background-size: contain;
			display: inline-block;
			content: '';
			height: 43px;
			margin-right: 1rem;
			vertical-align: middle;
			width: 43px;

			@include respond-to(md) {
				background-position: top left;
				height: 64px;
				left: -6rem;
				margin: 0;
				position: absolute;
				top: 0;
				width: 64px;
			}
		}

		&.heading--cash-flow {
			&::before {
				background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/Cash-Flow-HD.svg');
			}
		}

		&.heading--gain-potential {
			&::before {
				background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/Long-Term-Growth-HD.svg');
			}
		}

		&.heading--diversification {
			&::before {
				background-image: url($cloud-front-domain + '/images/marketing-site/reiguide/Diversification-HD.svg');
			}
		}
	}
}
</style>
